import { Chart as G2Chart } from '@antv/g2';
import { forwardRef, HTMLAttributes, Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { IBaseChartProps, IComponent } from '../../../models/components.models';
import { useClassNames } from '../../../utils/hooks';
import { areaChartStylesDefault, IAreaChartClassNames, IAreaChartStyles } from './AreaChart.styles';

const render = (
  el: HTMLElement,
  data: { [key: string]: any }[],
  labelFieldName: string,
  valueFieldNames: string[],
  colors: string[] = ['blue'],
  gradient?: {
    start?: string;
    end?: string;
  }
) => {
  let style: { [key: string]: string } = {};
  if (gradient) {
    style = { fill: `linear-gradient(90deg, ${gradient.end} 0%, ${gradient.start} 100% )` };
  }
  const chart = new G2Chart({ container: el, autoFit: true });
  chart.data(data);
  chart.area().encode('x', labelFieldName).encode('y', valueFieldNames[0]).style(style);
  chart.line().encode('x', labelFieldName).encode('y', valueFieldNames[0]).style({
    stroke: colors[0],
    strokeWidth: 2,
  });
  chart.render();
};

export interface IAreaChartProps extends IComponent<IAreaChartStyles>, HTMLAttributes<HTMLDivElement>, IBaseChartProps {
  colors?: string[];
  gradient?: {
    start?: string;
    end?: string;
  };
}

export const AreaChart = forwardRef((props: IAreaChartProps, ref: Ref<HTMLDivElement>) => {
  // some variables are not used, but they need to get `elementProps`.
  const {
    className,
    styles,
    data = [],
    colors,
    gradient,
    labelFieldName = '',
    valueFieldNames = [],
    ...elementProps
  } = props;
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  useEffect(() => {
    if (refRoot.current) {
      render(refRoot.current, data, labelFieldName, valueFieldNames, colors, gradient);
    }
  }, [data, labelFieldName, valueFieldNames, colors, gradient]);

  const classNames = useClassNames<Partial<IAreaChartStyles>, IAreaChartClassNames>(
    'AreaChart',
    areaChartStylesDefault,
    styles,
    className
  );
  return <div {...elementProps} ref={refRoot} className={classNames.root} />;
});
